/**
 * Michelangelo Phase 2: IPCAF Self-Assessment Form
 * 8 components, 81 questions — WHO Infection Prevention and Control Assessment Framework.
 * Layout: shared sidebar + header pill + question cards (WHOAssessment.css).
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import assessmentInstrumentService from '../../services/assessmentInstrumentService';
import hospitalCapabilityProfileService from '../../services/hospitalCapabilityProfileService';
import { useNotifications } from '../NotificationSystem';
import '../who/WHOAssessment.css';
import './IPCAFAssessment.css';

const INSTRUMENT_ID = 'IPCAF_2019';

const IPCAFAssessment = ({ organizationId, userId, userName, onComplete }) => {
  const { t, i18n } = useTranslation(['michelangelo', 'common']);
  const { notify } = useNotifications();
  const [instrument, setInstrument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [activeComponentIndex, setActiveComponentIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const useRo = (i18n.language || 'ro').startsWith('ro');

  useEffect(() => {
    let cancelled = false;
    assessmentInstrumentService.getInstrument(INSTRUMENT_ID).then((data) => {
      if (!cancelled) {
        setInstrument(data);
        setLoading(false);
      }
    }).catch((err) => {
      if (!cancelled) {
        console.error('IPCAF load:', err);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const handleAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    if (!organizationId || !userId) {
      notify.error(t('michelangelo:selectOrganization') || 'Select an organization.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await hospitalCapabilityProfileService.submitIPCAF(
        organizationId,
        answers,
        userId,
        userName,
        INSTRUMENT_ID
      );
      notify.success(t('michelangelo:ipcafSubmitted') || 'IPCAF assessment submitted.');
      onComplete?.(result);
    } catch (err) {
      notify.error(err.message || t('common:saveFailed') || 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="ipcaf-assessment loading">
        <p>{t('michelangelo:loading') || 'Loading IPCAF…'}</p>
      </div>
    );
  }

  if (!instrument) {
    return (
      <div className="ipcaf-assessment error">
        <p>{t('michelangelo:ipcafNotFound') || 'IPCAF instrument not found. Run seed-ipcaf to load it.'}</p>
      </div>
    );
  }

  const components = instrument.components || [];
  const activeComp = components[activeComponentIndex];
  const totalQuestions = components.reduce((s, c) => s + (c.questions?.length || 0), 0);
  const answeredCount = Object.keys(answers).filter(
    (k) => answers[k] !== undefined && answers[k] !== null && answers[k] !== ''
  ).length;
  const canSubmit = answeredCount >= totalQuestions;

  return (
    <div className="ipcaf-assessment who-assessment">
      <div className="who-assessment-header-row">
        <div className="who-assessment-header-titles">
          <h2>{useRo ? (instrument.nameRo || instrument.name) : (instrument.name || instrument.nameRo)}</h2>
          {(instrument.descriptionRo || instrument.description) && (
            <p className="who-assessment-header-subtitle">
              {useRo ? (instrument.descriptionRo || instrument.description) : (instrument.description || instrument.descriptionRo)}
            </p>
          )}
        </div>
        <div className="who-assessment-header-meta">
          <span className="who-assessment-pill" aria-label={t('michelangelo:questionsAnswered')}>
            {answeredCount} / {totalQuestions} {useRo ? 'răspunse' : 'answered'}
          </span>
          <div className="who-assessment-submit-wrap">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
            >
              {submitting
                ? (t('michelangelo:submitting') || 'Submitting…')
                : (t('michelangelo:submitAssessment') || 'Submit Assessment')}
            </button>
          </div>
        </div>
      </div>

      <div className="who-assessment-with-sidebar">
        <nav className="who-assessment-sidebar" aria-label={t('michelangelo:components') || 'Components'}>
          <div className="who-assessment-sidebar-nav">
            {components.map((comp, idx) => {
              const compQuestions = comp.questions || [];
              const compAnswered = compQuestions.filter(
                (q) => answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== ''
              ).length;
              const isCompleted = compQuestions.length > 0 && compAnswered === compQuestions.length;
              return (
                <button
                  key={comp.id}
                  type="button"
                  className={`who-assessment-sidebar-item ${idx === activeComponentIndex ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                  onClick={() => setActiveComponentIndex(idx)}
                >
                  <span className="who-assessment-sidebar-item-name">
                    {comp.code}. {useRo ? (comp.nameRo || comp.nameEn) : (comp.nameEn || comp.nameRo)}
                  </span>
                  <span className="who-assessment-sidebar-item-count">
                    {compAnswered} / {compQuestions.length}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        <main className="who-assessment-main">
          {activeComp && (
            <>
              <div className="who-assessment-chapter-divider">
                <span className="who-assessment-chapter-badge">{activeComp.code}</span>
                <span className="who-assessment-chapter-line" />
              </div>
              {(activeComp.questions || []).map((q) => (
                <div key={q.id} className="who-assessment-question-card">
                  <div className="who-assessment-question-inner">
                    <span className="who-assessment-question-code">{q.code}</span>
                    <p className="who-assessment-question-text">
                      {useRo ? (q.textRo || q.textEn) : (q.textEn || q.textRo)}
                    </p>
                  </div>
                  <div className="q-options">
                    {(q.options || []).map((opt) => (
                      <label key={opt.value} className="q-option">
                        <input
                          type="radio"
                          name={q.id}
                          value={opt.value}
                          checked={answers[q.id] === opt.value}
                          onChange={() => handleAnswer(q.id, opt.value)}
                        />
                        <span>{useRo ? (opt.labelRo || opt.label) : (opt.label || opt.labelRo)}</span>
                        {opt.value !== undefined && typeof opt.value === 'number' && (
                          <span className="option-score">({opt.value})</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default IPCAFAssessment;
